import { useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { Archive, ArchiveRestore, Check, ChevronRight, Plus, Search, Trash2 } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Banner } from '@/components/Banner';
import { Button, BotonEnlace } from '@/components/Button';
import { EstadoVacio } from '@/components/Card';
import { NumberField, SelectField, TextField } from '@/components/Field';
import { Segmentado } from '@/components/Segmentado';
import { archivarEjercicio, borrarEjercicio, guardarEjercicio, leerEjercicio, leerEjercicios } from '@/db/ejercicios';
import { leerSettings } from '@/db/schema';
import { agregarEjercicioARutina } from '@/db/rutinas';
import { nuevoId } from '@/db/ids';
import { mensajeDeError } from '@/db/tx';
import type { Equipo, Exercise, GrupoMuscular, TipoEjercicio } from '@/db/types';
import { ETIQUETAS_EQUIPO, ETIQUETAS_GRUPO, ETIQUETAS_TIPO, ejercicioBase } from '@/datos/ejercicios-base';
import { normalizar } from '@/domain/busqueda';
import { ahoraIso } from '@/domain/dates';
import { formatearNumero, leerDecimal } from '@/domain/numbers';
import { ETIQUETAS_UNIDAD_CARGA, UNIDADES_CARGA, aKg, desdeKg, type UnidadCarga, type UnidadPeso } from '@/domain/units';
import { HistorialDeEjercicio } from './HistorialPages';
import { ProgresoDeEjercicio } from './ProgresoDeEjercicio';

const GRUPOS = Object.keys(ETIQUETAS_GRUPO) as GrupoMuscular[];

/** Coincide por nombre o por sus sinónimos, sin acentos ni mayúsculas (Req. 9.4). */
function coincide(x: Exercise, consulta: string): boolean {
  const q = normalizar(consulta);
  if (q === '') return true;
  const textos = [x.nombre, ...(ejercicioBase(x.id)?.sinonimos ?? [])].map(normalizar);
  return q.split(' ').every((palabra) => textos.some((t) => t.includes(palabra)));
}

/**
 * Ejercicios (Req. 9.2 a 9.4): buscar, filtrar por grupo, crear y editar.
 * Con `?para=rutina:<id>` o `?para=entrenamiento:<id>`, tocar uno lo agrega.
 */
export function EjerciciosPage() {
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const ejercicios = useLiveQuery(() => leerEjercicios(), []);
  const [consulta, setConsulta] = useState('');
  const [grupo, setGrupo] = useState<string>('todos');
  const [verArchivados, setVerArchivados] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const para = parametros.get('para');
  const [tipoDestino, idDestino] = para?.split(':') ?? [];
  const eligiendo = (tipoDestino === 'rutina' || tipoDestino === 'entrenamiento') && idDestino !== undefined;

  const visibles = useMemo(
    () =>
      (ejercicios ?? []).filter(
        (x) => (verArchivados && !eligiendo ? true : !x.archivado) && (grupo === 'todos' || x.grupoMuscular === grupo) && coincide(x, consulta),
      ),
    [ejercicios, consulta, grupo, verArchivados, eligiendo],
  );
  if (ejercicios === undefined) return null;

  async function elegir(x: Exercise) {
    if (!eligiendo || idDestino === undefined) return;
    setError(null);
    try {
      if (tipoDestino === 'rutina') {
        await agregarEjercicioARutina(idDestino, x.id);
        void navegar(`/gym/rutina/${idDestino}?editar=1`, { replace: true });
      } else {
        // Antes de agregarlo se confirma cómo se hace hoy: como en la rutina o a mano (0.3.8)
        void navegar(`/gym/entrenamiento/agregar/${encodeURIComponent(x.id)}`, { replace: true });
      }
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  const volver = eligiendo
    ? tipoDestino === 'rutina'
      ? { a: `/gym/rutina/${idDestino}?editar=1`, texto: 'Rutina' }
      : { a: '/gym/entrenamiento', texto: 'Entrenamiento' }
    : { a: '/gym', texto: 'Gym', etiqueta: 'Volver a Gym' };

  return (
    <div>
      <PageHeader
        titulo={eligiendo ? 'Elige un ejercicio.' : 'Ejercicios.'}
        descripcion={eligiendo ? 'Toca el que quieras agregar.' : 'El catálogo en español y los tuyos.'}
        volver={volver}
      />

      <div className="relative mb-3">
        <Search aria-hidden="true" className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-texto-suave" />
        <label htmlFor="buscar-ejercicio" className="sr-only">
          Buscar ejercicio
        </label>
        <input
          id="buscar-ejercicio"
          type="search"
          value={consulta}
          onChange={(e) => setConsulta(e.target.value)}
          placeholder="Buscar: prensa, jalón, curl…"
          className="min-h-12 w-full rounded-[13px] border border-borde-fuerte bg-superficie pl-10 pr-3 text-[15px] text-texto placeholder:text-texto-suave"
        />
      </div>
      <SelectField
        etiqueta="Grupo muscular"
        valor={grupo}
        onCambio={setGrupo}
        opciones={[{ valor: 'todos', etiqueta: 'Todos los grupos' }, ...GRUPOS.map((g) => ({ valor: g, etiqueta: ETIQUETAS_GRUPO[g] }))]}
      />

      {error !== null && (
        <div className="mt-3">
          <Banner tono="alerta" titulo="No se agregó">
            {error}
          </Banner>
        </div>
      )}

      <h2 className="antetitulo mt-6">{visibles.length === 1 ? '1 ejercicio' : `${visibles.length} ejercicios`}</h2>
      {visibles.length === 0 ? (
        <EstadoVacio titulo="Sin resultados." descripcion="Prueba con otra palabra, o crea el ejercicio." />
      ) : (
        <ul className="divide-y divide-borde">
          {visibles.map((x) => {
            const contenido = (
              <>
                <span className="grid min-w-0 flex-1 gap-1">
                  <span className="text-[14px] font-medium text-texto">{x.nombre}</span>
                  <span className="text-[12px] text-texto-suave">
                    {[ETIQUETAS_GRUPO[x.grupoMuscular], ETIQUETAS_EQUIPO[x.equipo], x.esBase ? null : 'Mío', x.archivado ? 'Archivado' : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                {eligiendo ? <Plus className="size-4 text-marca" /> : <ChevronRight className="size-4 text-texto-suave" />}
              </>
            );
            return (
              <li key={x.id}>
                {eligiendo ? (
                  <button type="button" onClick={() => void elegir(x)} className="flex min-h-14 w-full items-center gap-3 py-3 text-left active:opacity-60">
                    {contenido}
                  </button>
                ) : (
                  <Link to={`/gym/ejercicios/${x.id}`} className="flex min-h-14 items-center gap-3 py-3 active:opacity-60">
                    {contenido}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-6 grid gap-2">
        <BotonEnlace
          to={`/gym/ejercicios/nuevo${para === null ? '' : `?para=${encodeURIComponent(para)}`}`}
          variante="secundario"
          icono={Plus}
        >
          Crear ejercicio
        </BotonEnlace>
        {!eligiendo && (
          <Button variante="fantasma" icono={Archive} onClick={() => setVerArchivados((v) => !v)}>
            {verArchivados ? 'Ocultar archivados' : 'Ver archivados'}
          </Button>
        )}
      </div>
    </div>
  );
}

/** Crear o editar un ejercicio (Req. 9.3, 9.5, 9.6). Del catálogo base solo cambian unidad y notas. */
export function EjercicioPage() {
  const { id } = useParams();
  const existente = useLiveQuery(async () => (id === undefined ? null : ((await leerEjercicio(id)) ?? null)), [id]);
  const [idNuevo] = useState(() => nuevoId('ej'));
  if (existente === undefined) return null;
  if (id !== undefined && existente === null) {
    return (
      <div>
        <PageHeader titulo="No está." volver={{ a: '/gym/ejercicios', texto: 'Ejercicios' }} />
        <EstadoVacio titulo="Ese ejercicio ya no existe." />
      </div>
    );
  }
  const ahora = ahoraIso();
  const inicial: Exercise = existente ?? {
    id: idNuevo,
    nombre: '',
    grupoMuscular: 'gluteos',
    equipo: 'maquina',
    tipo: 'peso_reps',
    esBase: false,
    unidadCaptura: null,
    incrementoKg: null,
    notas: null,
    archivado: false,
    creadoEn: ahora,
    actualizadoEn: ahora,
  };
  return <FormularioEjercicio key={inicial.id} inicial={inicial} nuevo={existente === null} />;
}

function FormularioEjercicio({ inicial, nuevo }: { inicial: Exercise; nuevo: boolean }) {
  const navegar = useNavigate();
  const [parametros] = useSearchParams();
  const para = parametros.get('para');
  const ajustes = useLiveQuery(() => leerSettings(), []);
  const [nombre, setNombre] = useState(inicial.nombre);
  const [grupo, setGrupo] = useState<GrupoMuscular>(inicial.grupoMuscular);
  const [equipo, setEquipo] = useState<Equipo>(inicial.equipo);
  const [tipo, setTipo] = useState<TipoEjercicio>(inicial.tipo);
  const [unidad, setUnidad] = useState<UnidadCarga | 'preferida'>(inicial.unidadCaptura ?? 'preferida');
  const [notas, setNotas] = useState(inicial.notas ?? '');
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);
  const base = inicial.esBase;

  // El incremento para la sugerencia de progresión (Req. 26.1), en la unidad
  // con que se captura la carga de este ejercicio. Vacío = el predeterminado
  const preferida = ajustes?.unidadPeso ?? 'kg';
  const deLaCarga: UnidadPeso = unidad === 'preferida' ? preferida : unidad === 'placa' ? preferida : unidad;
  const [incremento, setIncremento] = useState(
    inicial.incrementoKg === null ? '' : formatearNumero(desdeKg(inicial.incrementoKg, inicial.unidadCaptura === 'lb' ? 'lb' : 'kg'), { decimales: 2, fijos: false }),
  );

  async function guardar(evento: FormEvent) {
    evento.preventDefault();
    if (guardando) return;
    setGuardando(true);
    setError(null);
    try {
      const suyo = incremento.trim() === '' ? null : leerDecimal(incremento);
      if (incremento.trim() !== '' && (suyo === null || suyo <= 0)) {
        setGuardando(false);
        return setError('El incremento debe ser un número mayor que cero, o déjalo vacío.');
      }
      await guardarEjercicio({
        ...inicial,
        nombre,
        grupoMuscular: grupo,
        equipo,
        tipo,
        unidadCaptura: unidad === 'preferida' ? null : unidad,
        incrementoKg: suyo === null ? null : aKg(suyo, deLaCarga),
        notas: notas.trim() === '' ? null : notas,
      });
      const [tipoDestino, idDestino] = para?.split(':') ?? [];
      if (nuevo && idDestino !== undefined && tipoDestino === 'rutina') {
        await agregarEjercicioARutina(idDestino, inicial.id);
        void navegar(`/gym/rutina/${idDestino}?editar=1`, { replace: true });
      } else if (nuevo && idDestino !== undefined && tipoDestino === 'entrenamiento') {
        void navegar(`/gym/entrenamiento/agregar/${encodeURIComponent(inicial.id)}`, { replace: true });
      } else {
        void navegar('/gym/ejercicios', { replace: true });
      }
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  async function accion(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      void navegar('/gym/ejercicios', { replace: true });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  return (
    <div>
      <PageHeader
        titulo={nuevo ? 'Ejercicio nuevo.' : base ? inicial.nombre : 'Tu ejercicio.'}
        descripcion={base ? 'Del catálogo: puedes cambiar su unidad de carga y tus notas.' : 'Con su grupo, equipo y tipo.'}
        volver={{ a: '/gym/ejercicios', texto: 'Ejercicios' }}
      />
      <form onSubmit={(e) => void guardar(e)} className="grid gap-[17px]" noValidate>
        {!base && (
          <>
            <TextField etiqueta="Nombre" valor={nombre} onCambio={setNombre} maxLength={80} placeholder="Prensa del gym nuevo" />
            <SelectField
              etiqueta="Grupo muscular"
              valor={grupo}
              onCambio={(v) => setGrupo(v as GrupoMuscular)}
              opciones={GRUPOS.map((g) => ({ valor: g, etiqueta: ETIQUETAS_GRUPO[g] }))}
            />
            <SelectField
              etiqueta="Equipo"
              valor={equipo}
              onCambio={(v) => setEquipo(v as Equipo)}
              opciones={(Object.keys(ETIQUETAS_EQUIPO) as Equipo[]).map((x) => ({ valor: x, etiqueta: ETIQUETAS_EQUIPO[x] }))}
            />
            <SelectField
              etiqueta="Tipo"
              valor={tipo}
              onCambio={(v) => setTipo(v as TipoEjercicio)}
              opciones={(Object.keys(ETIQUETAS_TIPO) as TipoEjercicio[]).map((x) => ({ valor: x, etiqueta: ETIQUETAS_TIPO[x] }))}
            />
          </>
        )}
        {tipo === 'peso_reps' && (
          <Segmentado
            etiqueta="Unidad de carga"
            etiquetaVisible
            completo
            valor={unidad}
            onCambio={setUnidad}
            opciones={[
              { valor: 'preferida', etiqueta: 'La mía' },
              ...UNIDADES_CARGA.map((u) => ({ valor: u, etiqueta: ETIQUETAS_UNIDAD_CARGA[u] })),
            ]}
          />
        )}
        {tipo === 'peso_reps' && (
          <NumberField
            etiqueta={`Incremento para subir (${deLaCarga}, opcional)`}
            valor={incremento}
            onCambio={setIncremento}
            ayuda={`Lo que subes cuando llegas al tope del rango en todas las series. Vacío usa ${
              deLaCarga === 'lb' ? '5 lb' : `${formatearNumero(ajustes?.incrementoPredeterminadoKg ?? 2.5, { decimales: 2, fijos: false })} kg`
            }.`}
          />
        )}
        <TextField etiqueta="Notas (opcional)" valor={notas} onCambio={setNotas} maxLength={300} placeholder="Asiento en 4" />
        {error !== null && (
          <Banner tono="alerta" titulo="No se guardó">
            {error}
          </Banner>
        )}
        <Button type="submit" icono={Check} disabled={guardando}>
          {guardando ? 'Guardando…' : nuevo ? 'Guardar ejercicio' : 'Guardar cambios'}
        </Button>
        {!nuevo && (
          <Button
            variante="secundario"
            icono={inicial.archivado ? ArchiveRestore : Archive}
            onClick={() => void accion(() => archivarEjercicio(inicial.id, !inicial.archivado))}
          >
            {inicial.archivado ? 'Sacar del archivo' : 'Archivar'}
          </Button>
        )}
        {!nuevo && !base && (
          <Button variante="fantasma" icono={Trash2} onClick={() => void accion(() => borrarEjercicio(inicial.id))}>
            Borrar (si ya se usó, se archiva)
          </Button>
        )}
      </form>
      {!nuevo && (
        <div className="mt-8 space-y-8">
          <ProgresoDeEjercicio exerciseId={inicial.id} />
          <HistorialDeEjercicio exerciseId={inicial.id} />
        </div>
      )}
    </div>
  );
}
